import React, { useEffect, useState } from 'react';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import auth from '../services/auth';
import { useIsMobile } from '../theme';

// Mirrors MIN_PASSWORD_LENGTH in the server controller, which is the real
// gate — this only saves a round trip.
const MIN_LENGTH = 8;

const EMPTY = { currentPassword: '', newPassword: '', repeatPassword: '' };

export default function ChangePasswordDialog({ open, onClose, onSuccess }) {
    const isMobile = useIsMobile();
    const [form, setForm] = useState(EMPTY);
    const [reveal, setReveal] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    // Never leave a typed password sitting in state after the dialog closes.
    useEffect(() => {
        if (!open) {
            setForm(EMPTY);
            setReveal(false);
            setError(null);
            setSaving(false);
        }
    }, [open]);

    const setField = (field) => (event) => {
        setForm((prev) => ({ ...prev, [field]: event.target.value }));
        setError(null);
    };

    const mismatch = !!form.repeatPassword && form.newPassword !== form.repeatPassword;
    const tooShort = !!form.newPassword && form.newPassword.length < MIN_LENGTH;
    const canSave = form.currentPassword && form.newPassword && !mismatch && !tooShort && !saving;

    const handleSave = async () => {
        setSaving(true);
        setError(null);
        try {
            await auth.changePassword(form.currentPassword, form.newPassword);
            onSuccess && onSuccess();
            onClose();
        } catch (err) {
            setError(err.response?.data?.error || 'Não foi possível alterar a palavra-passe.');
            setSaving(false);
        }
    };

    const revealAdornment = (
        <InputAdornment position='end'>
            <IconButton onClick={() => setReveal((value) => !value)} edge='end' aria-label='Mostrar palavras-passe' tabIndex={-1}>
                {reveal ? <VisibilityOff /> : <Visibility />}
            </IconButton>
        </InputAdornment>
    );

    return (
        <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth='xs' fullWidth fullScreen={isMobile}>
            <DialogTitle>Alterar palavra-passe</DialogTitle>
            <DialogContent dividers>
                <Box
                    component='form'
                    onSubmit={(event) => {
                        event.preventDefault();
                        if (canSave) handleSave();
                    }}
                    sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}
                >
                    {error && <Alert severity='error'>{error}</Alert>}

                    <TextField
                        label='Palavra-passe atual'
                        type={reveal ? 'text' : 'password'}
                        value={form.currentPassword}
                        onChange={setField('currentPassword')}
                        autoComplete='current-password'
                        autoFocus
                        fullWidth
                        InputProps={{ endAdornment: revealAdornment }}
                    />
                    <TextField
                        label='Nova palavra-passe'
                        type={reveal ? 'text' : 'password'}
                        value={form.newPassword}
                        onChange={setField('newPassword')}
                        autoComplete='new-password'
                        error={tooShort}
                        helperText={tooShort ? `Mínimo ${MIN_LENGTH} caracteres` : `Pelo menos ${MIN_LENGTH} caracteres`}
                        fullWidth
                    />
                    <TextField
                        label='Repetir a nova palavra-passe'
                        type={reveal ? 'text' : 'password'}
                        value={form.repeatPassword}
                        onChange={setField('repeatPassword')}
                        autoComplete='new-password'
                        error={mismatch}
                        helperText={mismatch ? 'As palavras-passe não coincidem' : ' '}
                        fullWidth
                    />

                    <Typography variant='caption' color='text.secondary'>
                        A sessão atual mantém-se aberta. Só terá de usar a nova palavra-passe no próximo início de sessão.
                    </Typography>

                    {/* Lets Enter submit the form without a visible second button. */}
                    <button type='submit' hidden />
                </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 'calc(16px + env(safe-area-inset-bottom))' }}>
                <Button onClick={onClose} disabled={saving} color='inherit'>
                    Cancelar
                </Button>
                <Button onClick={handleSave} variant='contained' disabled={!canSave}>
                    {saving ? 'A guardar…' : 'Guardar'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
